import { createContext, useContext, type ReactNode } from "react";

/**
 * PanelHostContext — expose le conteneur DOM d'un panel a ses descendants.
 *
 * Pourquoi : les primitives `<Modal>` et `<Drawer>` rendent via React portal.
 * Avant 2026-05-28 elles portalaient TOUJOURS sur `document.body` avec
 * `position: fixed inset-0` → la modale couvrait tout le viewport, ecrasant
 * visuellement les autres panels d'un layout multi-panneaux desktop pour
 * une action locale a UN panneau.
 *
 * Avec ce contexte, n'importe quel descendant d'un panel peut recuperer
 * l'element DOM du panel et y ancrer une modale (`absolute inset-0` au lieu
 * de `fixed`). Le shell desktop (`TabGroup`) fournit le contexte, le shell
 * mobile (`MobileShell`) ne le fournit PAS — Modal/Drawer retombent alors
 * sur leur comportement viewport-fullscreen historique (correct sur mobile
 * puisqu'il n'y a qu'un panel visible a la fois).
 *
 * Usage par le shell :
 *   <PanelHostProvider container={panelBodyRef.current}>
 *     <PanelHost instance={panel} />
 *   </PanelHostProvider>
 *
 * Usage par une primitive consommatrice :
 *   const { container } = usePanelHost();
 *   if (container) {
 *     // portal vers container, absolute inset-0
 *   } else {
 *     // portal vers document.body, fixed inset-0
 *   }
 */

interface PanelHostContextValue {
  /** Le `<div className="cockpit-panel-body">` qui heberge le panel actif.
   *  Null tant que le ref callback n'a pas resolu (1er render). */
  container: HTMLElement | null;
}

const PanelHostContext = createContext<PanelHostContextValue>({
  container: null,
});

interface PanelHostProviderProps {
  container: HTMLElement | null;
  children: ReactNode;
}

export function PanelHostProvider({
  container,
  children,
}: PanelHostProviderProps) {
  // Une closure par-render : `container` est passe en prop, donc l'identite
  // change uniquement quand le ref resoud (null → element). Pas besoin de
  // useMemo : le hot path est le re-render des panels, pas des modales.
  return (
    <PanelHostContext.Provider value={{ container }}>
      {children}
    </PanelHostContext.Provider>
  );
}

export function usePanelHost(): PanelHostContextValue {
  return useContext(PanelHostContext);
}
